import Dialog from "@mui/material/Dialog";
import Paper from '@mui/material/Paper';
import {WindowBorder} from "./WindowBorder";
import React, {PropsWithChildren, ReactNode, useCallback, useMemo, useState} from "react";
import {ModalKey} from 'module/Modal/type/ModalKey';
import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';
import {Tooltip, useTheme} from '@mui/material';
import {ModalPosition, ModalSize} from 'module/Modal/reducer/ModalType';
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import {isMobile} from "react-device-detect";
import {css} from '@emotion/react'

const cssPaperFitted = css`
	margin: 0 !important;
	width: 100%;
	height: 100%;
	max-width: none !important;
	max-height: none !important;
`

const PopupBodyComponent = React.memo(({
	propsWindowBorder,
	title,
	padContent,
	onClose,
	children,
	...props
}: any) => {
	const theme = useTheme()
	const cssScrollContent = css`
		width: 100%;
		height: 100%;
		overflow: auto;
		background: inherit;
		box-sizing: border-box;
		padding: ${padContent ? theme.spacing(1, 3) : ''};
	`
	const cssTitle = css`
		display: flex;
		justify-content: space-between;
		flex-wrap: nowrap;
		align-items: center;
		cursor: default;
		font-size: 1.2rem;
		border-radius: 0;
		border: 1px solid #d2d1d1;
		padding: ${theme.spacing(0.5, 0.5)};
	`
	const cssTitleText = css`
		flex-grow: 1;
		overflow: auto;
		padding: ${theme.spacing(0, 0, 0, 2)}
	`
	const cssTitleAction = css`
		display: flex;
		justify-content: center;
		flex-Wrap: wrap-reverse;
	`

	const {defaultFullScreen: {fullScreen, mobile}, onFullScreen} = propsWindowBorder
	return (
		<WindowBorder {...propsWindowBorder}>
			<Paper {...props} css={cssPaperFitted}>
				{title && <Paper>
					<div css={cssTitle}>
						<div css={cssTitleText}>
							{title}
						</div>
						<div css={cssTitleAction}>
							{!mobile && <Tooltip disableInteractive title={!fullScreen ? 'Maximieren' : 'Verkleinern'}>
								<IconButton onClick={onFullScreen} size={"small"}>
									{!fullScreen && <FullscreenIcon/> || <FullscreenExitIcon/>}
								</IconButton>
							</Tooltip>}
							<IconButton size={"small"} onClick={onClose}><CloseIcon/></IconButton>
						</div>
					</div>
				</Paper>}
				<div css={cssScrollContent}>
					{children}
				</div>
			</Paper>
		</WindowBorder>
	)
})

export interface ModalComponentProps extends PropsWithChildren<{}> {
	modalKey?: ModalKey
	open: boolean
	title?: string | ReactNode
	defaultPosition?: { x?: number, y?: number }
	defaultSize?: { height?: number, width?: number }
	minWidth?: number
	minHeight?: number
	/** for confirm and alert dialogs 2500 to keep them always on top */
	zIndex?: number
	keepMounted?: boolean
	disableAutoFocus?: boolean
	closeOnBackdropClick?: boolean
	/**
	 * use on dialogs with dropdown
	 */
	doubleEscClose?: boolean
	onClose: React.ReactEventHandler
	onChangedPositionSize?: (position?: ModalPosition, size?: ModalSize) => void
	padContent?: boolean
	fullScreen?: boolean
	onFullScreen?: () => void
}

export const ModalComponent = React.memo(({
	modalKey,
	open = false,
	title,
	defaultPosition: propsDefaultPosition = {},
	defaultSize: propsDefaultSize = {},
	minWidth = 50,
	minHeight = 50,
	zIndex,
	keepMounted,
	disableAutoFocus,
	closeOnBackdropClick = true,
	doubleEscClose = false,
	fullScreen: propsDefaultFullscreen = false,
	padContent,
	onClose: externalOnClose,
	onChangedPositionSize,
	onFullScreen,
	children,
	...otherProps
}: ModalComponentProps) => {
	let defaultSize = {
		width: propsDefaultSize.width || Math.max(Math.floor(window.innerWidth * 0.6), 500),
		height: propsDefaultSize.height || Math.max(Math.floor(window.innerHeight * 0.8), 600),
	}
	let defaultPosition = {
		x: propsDefaultPosition.x === 0 ? 0 : propsDefaultPosition.x || Math.max((window.innerWidth - defaultSize.width) / 2, 0),
		y: propsDefaultPosition.y === 0 ? 0 : propsDefaultPosition.y || Math.max((window.innerHeight - defaultSize.height) / 2, 0),
	}

	let defaultFullScreen = {
		mobile: isMobile,
		fullScreen: propsDefaultFullscreen || isMobile,
	}

	const propsWindowBorder = useMemo(() => ({
		modalKey,
		minWidth,
		minHeight,
		defaultSize,
		defaultPosition,
		onChangedPositionSize,
		defaultFullScreen,
		onFullScreen,
	}), [modalKey, minWidth, minHeight, defaultSize, defaultPosition, defaultFullScreen, onFullScreen, onChangedPositionSize]);

	const [escCount, setEscCount] = useState(0)

	const onClose = useCallback((evt: any, reason?: 'escapeKeyDown' | 'backdropClick') => {
		switch (reason) {
			case 'backdropClick':
				if (closeOnBackdropClick) externalOnClose(evt)
				break;
			case 'escapeKeyDown':
				if (!doubleEscClose) {
					externalOnClose(evt)
				} else {
					if (escCount > 0) {
						externalOnClose(evt)
					} else {
						setEscCount(escCount + 1);
						setTimeout(() => setEscCount(0), 400)
					}
				}
				break;
			default:
				externalOnClose(evt)
		}
	}, [externalOnClose, escCount, doubleEscClose, closeOnBackdropClick])

	return <Dialog
		{...otherProps}
		style={typeof zIndex === 'number' ? {zIndex} : {}}
		open={open}
		keepMounted={keepMounted}
		disableAutoFocus={disableAutoFocus}
		onClose={onClose}
		PaperComponent={PopupBodyComponent}
		PaperProps={{propsWindowBorder, title, onClose, padContent} as any}
	>
		{children}
	</Dialog>
})
